import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment as env } from '../../../environments/environment';
import { AgGridAngular } from 'ag-grid-angular';
import { themeBalham } from 'ag-grid-community';
import { colorSchemeDark, colorSchemeLight } from 'ag-grid-community';
import { DateTime } from 'luxon';
import * as _ from 'lodash';
import { ToastService } from '../../@shared/toast.service';
import { io } from 'socket.io-client';
import { RestService } from '../../@shared/rest.service';
import { LoadingService } from '../../@shared/loading.service';

const SCAN_6 = 'Scan_6';
const SCAN_7_9 = 'Scan_7_9';
const SCAN_ALL_10 = 'Scan_10';
const SCAN_2_YEARS = 'Scan_2_Years';

@Component({
  selector: 'app-option-sniper-t3',
  imports: [AgGridAngular, CommonModule, FormsModule],
  templateUrl: './option-sniper-t3.component.html',
  styleUrl: './option-sniper-t3.component.scss'
})
export class OptionSniperT3Component implements OnDestroy {
  _token: any;
  _socket: any;
  _userData: any;
  _scannerAccess: any;
  _baseUrl = env.api_url;

  darkMode = true;
  gridDarkTheme = themeBalham
    .withPart(colorSchemeDark)
    .withParams({
      backgroundColor: '#1c1924',
      foregroundColor: '#d8d8d8',
      accentColor: '#5C7A98',
      headerTextColor: '#8387b7',
      headerFontSize: 15,
      oddRowBackgroundColor: 'rgb(255, 255, 255, 0.03)',
      spacing: '4px',
      wrapperBorderRadius: 2,
      fontSize: 15
    });
  gridLightTheme = themeBalham
    .withPart(colorSchemeLight)
    .withParams({
      accentColor: "#9C9CFF",
      backgroundColor: "#FEFEFE",
      browserColorScheme: "light",
      headerBackgroundColor: "#F3F4F6",
      headerFontSize: 15,
      headerTextColor: "#6e6e6e",
      headerFontWeight: 500,
      oddRowBackgroundColor: "#F7F7F7",
      spacing: 4,
      wrapperBorderRadius: 2,
      fontSize: 15
  });
  theme: any;

  dateMode = 'live';
  today = DateTime.now().toFormat('MM/dd/yyyy');
  current_week = 0;
  tier = {
    access: '',
    level: ''
  };
  scans: any[] = [];
  scansMap = {} as any;
  current_scan: any = null;
  grid: any;
  data: any[] = [];
  filtered_data: any[] = [];

  show_reports = false;
  available_reports: any[] = [];
  selected_report: any;
  report_symbols: any[] = [];
  report: any;
  report_data: any[] = [];
  report_symbol: string = '';
  report_type: string = 'all';  
  report_grid: any = {};

  constructor(private toast: ToastService,
    private restService: RestService,
    private loadingService: LoadingService
  ) {
    //Get the jwt token to authenticate
    const url = new URL(window.location.href);
    this._token = url.searchParams.get('token');
    if (!this._token) {
      //Try to get it from local storage
      this._token = localStorage.getItem('f2-token');
      if (!this._token) {
        this.toast.error('Error', 'JWT token missing from URL');
        return;
      }
    }

    this.theme = this.darkMode ? this.gridDarkTheme : this.gridLightTheme;
    this.init_grid();
    this.set_grid_data();
    this.init_socket();
  }

  ngOnDestroy() {
    this.reset_socket();
  }

  /*** Socket IO ***/
  async init_socket() {

    let _id = this.loadingService.add('Establishing data connection ...');

    this.reset_socket();
    this._socket = io(`wss://${this._baseUrl}`,
    {
      reconnectionDelayMax: 5000,
      auth: {
        token: this._token
      },
      transports: ['websocket', 'polling'],
    });

    //Set event handlers
    this._socket.on('connect', (res: any) => {
      if (!env.production) console.log('Socket connected');
      this.loadingService.remove(_id);
    });
    this._socket.on('connect_error', (error: any) => {
      if (!env.production) console.log('Socket connect_error:', error);
      this.toast.error('Authentication Error', 'Invalid token');
      this.loadingService.remove(_id);
    });
    this._socket.on('disconnect', () => {
      if (!env.production) console.log('Socket disconnected');
    });
    this._socket.on('reconnect', (attempt: any) => {
      if (!env.production) console.log('Socket reconnected. Attempt:', attempt);
    });
    this._socket.on('reconnect_attempt', (attempt: any) => {
      if (!env.production) console.log('Socket reconnect_attempt:', attempt);
    });
    this._socket.on('reconnect_failed', () => {
      if (!env.production) console.log('Socket reconnect_failed');
    });
    this._socket.on('reconnect_error', (error: any) => {
      if (!env.production) console.log('Socket reconnect_error:', error);
    });
    this._socket.on('error', (error: any) => {
      if (!env.production) console.log('Socket error', error);
      this.toast.error('Socket Error', error);
    });

    //Setting events
    this._socket.on('settings:scanner-access', (res: any) => {
      this.handle_settings_scanner_access(res);
    });
    this._socket.on('settings:user', (res: any) => {
      this.handle_settings_user(res);
    });

    //Tick alert events
    this._socket.on(`tickalert:join`, (res: any) => {
      this.handle_tick_alert_join(res);
    });
    this._socket.on(`tickalert:update`, (res: any) => {
      this.handle_tick_alert_update(res);
    });
    this._socket.on(`tickalert:refresh`, (res: any) => {
      if (!env.production) console.log('tickalert:refresh', res);
    });

    //Stateview events
    this._socket.on(`stateview:join`, (res: any) => {
      if (!env.production) console.log('stateview:join', res);
    });
    this._socket.on(`stateview:update`, (res: any) => {
      if (!env.production) console.log('update stateview', res);
    });
    this._socket.on(`stateview:refresh`, (res: any) => {
      if (!env.production) console.log('stateview:refresh', res);
    });
    this._socket.on(`stateview:delete`, (res: any) => {
      if (!env.production) console.log('stateview:delete', res);
    });
    
    //Report events
    this._socket.on(`report:join`, (res: any) => {
      if (!env.production) console.log('report:join', res);
    });

    //Chart events
    this._socket.on(`chart:get`, (res: any) => {
      if (!env.production) console.log('chart:get', res);
    });

    // console.log(this._socket);
  }
  reset_socket() {
    if (!env.production) console.log('reset_socket()');
    //Reset all socket variables
    //Disconnect the socket if connected
    if (this._socket) {
      //First leave from all of the groups
      _.each(this.scansMap, (scan: any) => {
        this._socket.emit('tickalert:leave', {group: scan.group});
      });

      this._socket.disconnect();
      this._socket = null;
    }
  }

  handle_settings_user(res: any) {
    if (!env.production) console.log('settings:user', res);
    this._userData = res;
    this._userData._claims = {} as any;
    if (res.subscanner && res.subscanner.length) {
      _.each(res.subscanner, (sub: any) => {
        this._userData._claims[sub.name] = sub;
      });
    }

    this.tier.access = res.scanner_slug;
    let tokens = res.scanner_slug.split('-');
    if (tokens.length >= 3) {
      let t = tokens[2].toString().toUpperCase();
      switch(t) {
        case 'T1':
        case 'T2':
        case 'T3':
          this.tier.level = t;
          break;

        default:
          break;
      }
    }

    //Set visibility of rank column
    this.grid?.api?.setColumnsVisible(['Rank.value'], this.rank_allowed());
    //Now resize the columns
    setTimeout(() => {
      this.grid?.api?.sizeColumnsToFit();
    }, 100);

    this.setup_user_scanner_access();
    //TODO: Set the visiblity of reports
  }
  handle_settings_scanner_access(res: any) {
    if (!env.production) console.log('settings:scanner-access', res);
    this._scannerAccess = res;
    this.setup_user_scanner_access();
  }
  is_scanner_enabled(group: any) {
    let enabled = false;
    if (env.production === false) enabled = true;
    else if (this._userData?.role === 'admin') enabled = true;
    else {
      for(let i = 0; i < this._userData?.subscanner?.length; i++) {
        let sub = this._userData.subscanner[i];
        if (group.title.indexOf(sub.name) === 0) {
          enabled = true;
          break;
        }
      }
    }
    return enabled;
  }
  is_scanner_live(group: any) {
    let live = false;
    if (env.production === false) live = true;
    else if (this._userData?.role === 'admin') live = true;
    else {
      for(let i = 0; i < this._userData?.subscanner?.length; i++) {
        let sub = this._userData.subscanner[i];
        if (group.title.indexOf(sub.name) === 0) {
          live = sub.live;
          break;
        }
      }
    }
    return live;
  }
  setup_user_scanner_access() {
    if (!this._scannerAccess || !this._userData) {
      return;
    }

    for (let i = 0; i < this._scannerAccess.length; i++) {
      let sa = this._scannerAccess[i];
      // console.log(sa.slug);
      //! TEMP
      if (sa.slug !== 'option-sniper') continue;
      // console.log(sa);

      //Set the avaoilable reports
      this.available_reports = sa.allowedReports;
      this.available_reports.unshift('');

      _.each(sa.groups, (g: any) => {
        if (!g) return;
        // console.log(g.title, g.type)
        //! For now, ignore report groups
        if (g.type === 'report') return;
        // console.log(g.group);

        //! Join the group
        let s = {
          group: g.group,
          key: g.group,
          type: g.type,
          report: g.report,
          value: this.scans.length + 1,
          label: g.title,
          title: g.title,
          baseId: g.group.split('+')[0],
          break: g.group.indexOf('Over6') !== -1 ? true : false,
          _enabled: this.is_scanner_enabled(g),
          _live: this.is_scanner_live(g),
          _alert_count: 0
        };
        this.scansMap[g.group] = s;
        this.scans.push(s);

        if (s._enabled === true) {
          this._socket.emit('tickalert:join', {group: g.group});
        }

        //Set the initial scan
        if (!this.current_scan && s._enabled === true) {
          this.on_current_scan_change(g.group);
        }
      });
    }
    
    //Add the 6 scan option
    let scan_6 = {
      group: SCAN_6,
      key: SCAN_6,
      type: 'custom',
      report: null,
      value: this.scans.length + 1,
      label: 'Rank 6',
      baseId: SCAN_6,
      break: false,
      _enabled: this._userData.role === 'admin' ? true : this.rank_allowed(),
      _live: true,
      all_star: true,
      hide_report: true,
    };
    this.scansMap[scan_6.group] = scan_6;
    this.scans.push(scan_6);

    //Add the 7-9 scan option
    let scan_7_9 = {
      group: SCAN_7_9,
      key: SCAN_7_9,
      type: 'custom',
      report: null,
      value: this.scans.length + 1,
      label: 'Rank 7 - 9',
      baseId: SCAN_7_9,
      break: false,
      _enabled: this._userData.role === 'admin' ? true : this.rank_allowed(),
      _live: true,
      all_star: true,
      hide_report: true,
    };
    this.scansMap[scan_7_9.group] = scan_7_9;
    this.scans.push(scan_7_9);

    //Add the 10 scan option
    let scan_10 = {
      group: SCAN_ALL_10,
      key: SCAN_ALL_10,
      type: 'custom',
      report: null,
      value: this.scans.length + 1,
      label: 'Rank 10',
      baseId: SCAN_ALL_10,
      break: true,
      _enabled: this._userData.role === 'admin' ? true : this.rank_allowed(),
      _live: true,
      all_star: true,
      hide_report: true,
    };
    this.scansMap[scan_10.group] = scan_10;
    this.scans.push(scan_10);

    
    //Add the 2 years scan option
    let scan_2y = {
      group: SCAN_2_YEARS,
      key: SCAN_2_YEARS,
      type: 'custom',
      report: null,
      value: this.scans.length + 1,
      label: '2+ Years',
      baseId: SCAN_2_YEARS,
      break: false,
      _enabled: this._userData.role === 'admin' ? true : this.rank_allowed(),
      _live: true,
      all_star: true,
      hide_report: true,
    };
    this.scansMap[scan_2y.group] = scan_2y;
    this.scans.push(scan_2y);

    // console.log('scans:', this.scans);
    // console.log('scansMap:', this.scansMap);
  }
  
  handle_tick_alert_join(res: any) {
    // console.log('tickalert:join', res);
    let scan = this.scansMap[res.group];
    if (!scan) return;
    // console.log('handle_tick_alert_join', res)
    res.parsed_data = this.parse_data(scan.label, res.data);
    if (!scan) {
      this.scansMap[res.group] = res;
    }
    else {
      scan.data = res.data;
      scan.parsed_data = res.parsed_data;
    }
    // console.log(this.scansMap);

    if (res.group === this.current_scan) {
      this.set_grid_data();
    }
  }
  handle_tick_alert_update(res: any) {
    let scan = this.scansMap[res.group];
    if (!scan) return;
    if (!scan.parsed_data) scan.parsed_data = [];

    if (!res.data || !res.data.length) return;

    res.parsed_data = this.parse_data(scan.label, res.data);
    let pd = res.parsed_data[0];
    console.log('tickalert:update', `[${res.group}] ${res.settings.groupInfo.title} => ${res.data.length}`);
    // let log = [
    //   pd.stock_symbol,
    //   'Quantity: ' + pd.MultiTrade_Volume.value,
    //   'Strike: ' + pd.Strike.value,
    //   'Type: ' + pd.Type.value,
    //   "Price: " + pd.TradePrice.value,
    //   'Target: ' + pd.Target1?.value || null,
    //   'Rank: ' + pd.Rank.value,
    //   'Trade Count: ' + pd.TradeCount.value,
    // ].join(' ');
    // console.log('tickalert:update', `[${res.group}] ${res.settings.groupInfo.title}: ${res.data.length} => ${log}`);
    // console.log(res.data);

    if (this.current_scan === res.group) {
      // console.log('Updating grid data');
      //1.1 Update the data in the grid and in the array
      _.each(res.parsed_data, (d: any) => {
        if (!d) return;
        let node = this.grid.api.getRowNode(d.Id);
        if (node) {
          this.grid?.api?.applyTransaction({update: [d]});
        }
        else {
          this.grid?.api?.applyTransaction({add: [d]});
        }

        //Find the current item in the scan data
        let item = _.find(scan.parsed_data, (_d: any) => { return _d.Id === d.Id });
        if (!item) {
          //Add it
          scan.parsed_data.push(d);
        }
        else {
          //Update it
          item = d;
        }
      });
    }
    else {
      // console.log('Array data');
      //1.2 Update the data in the array
    _.each(res.parsed_data, (d: any) => {
      if (!d) return;
      //Find the current item in the scan data
      let item = _.find(scan.parsed_data, (_d: any) => { return _d?.Id === d.Id });
      if (!item) {
        //Add it
        scan.parsed_data.push(d);
        scan._alert_count++;
        //If the alert is recent (within the last 15 minutes, show a toast)
        if (Date.now() - d.exchange_time < 60000 * 15) {
          let msg = [
            d.stock_symbol,
            d.Type?.value || '',
            d.Strike?.value ? `Strike: ${d.Strike.value}` : '',
            d.Rank?.value ? `Rank: ${d.Rank.value}` : '',
          ].join('   ');
          this.toast.info(scan.label || scan.group, `${msg}`);
        }

        //Check if we need to add to custom scans
        if (d.Rank?.value >= 10) {
          //Add it to the Over_10 scan if not there already
          let over_10 = this.scansMap[SCAN_ALL_10];
          this.add_alert_to_custom_scan(over_10, d);
        }
        else if (d.Rank?.value >= 7 && d.Rank?.value <= 9) {
          //Add it to the 7-9 scan
          let scan_7_9 = this.scansMap[SCAN_7_9];
          this.add_alert_to_custom_scan(scan_7_9, d);
        }
        else if (d.Rank?.value === 6) {
          //Add it to the 6 scan
          let scan_6 = this.scansMap[SCAN_6];
          this.add_alert_to_custom_scan(scan_6, d);
        }

        if (d.last_alert_years >= 2) {
          //Add it to the 2 years scan
          let scan_2y = this.scansMap[SCAN_2_YEARS];
          this.add_alert_to_custom_scan(scan_2y, d);
        }
      }
      else {
        //Update it
        item = d;
      }
    });
    }
  }

  /*** General ***/
  toggleDarkMode() {
    this.darkMode = !this.darkMode;
    
    //Set the theme for the grid
    this.theme = this.darkMode ? this.gridDarkTheme : this.gridLightTheme;

    //Re-init the grid to re apply correct colors
    if (this.grid && this.grid.api) {
      this.grid.api.redrawRows();
    }
  }
  on_date_change(e: any) {
    // console.log('on_date_change()', e);
    this.today = DateTime.now().toFormat('MM/dd/yyyy');
    const value = e.target.value;
    if (this.dateMode === value) return;
    
    this.dateMode = value;
    //1. Unsubscribe from the current rooms
    _.each(this.scans, (scan: any) => {
      if (scan.type !== 'tickalert') return;
      // console.log('Leaving:', scan.group);
      this._socket.emit('tickalert:leave', {group: scan.group});
    });

    //2. Subscribe to the new rooms
    switch(this.dateMode) {
      case 'live':
      case this.today: {
        _.each(this.scans, (scan: any) => {
          if (scan._enabled !== true) return;
          if (scan.type !== 'tickalert') return;
          this._socket.emit('tickalert:join', {group: scan.group});
        });
        break;
      }
      default: {
        _.each(this.scans, (scan: any) => {
          if (scan._enabled !== true) return;
          if (scan.type !== 'tickalert') return;
          this._socket.emit('tickalert:join', {group: scan.group, date: this.dateMode});
        });
        break;
      }
    }

    setTimeout(() => {
      this.set_grid_data();
    }, 100);

    //If the reports are visible, re-init the report data
    if (this.show_reports === true) {
      this.init_scan_reports();
    }
  }
  getLiveColor() {
    let color = '#536878';
    if (this.dateMode === 'live' || this.dateMode === this.today) {
      if (this.darkMode) {
        color = '#629b00';
      }
      else {
        color = '#009831';
      }
    }

    return color;
  }
  on_current_scan_change(new_scan: any) {
    this.data = [];
    this.current_scan = new_scan;
    let scan = this.scansMap[this.current_scan];
    if (!scan || !scan._enabled) {
      return
    }
    
    //Reset the alert counter
    scan._alert_count = 0;

    //Show/hide the scan column based on current scan
    this.grid?.api?.setColumnsVisible(['scan'], (this.current_scan === SCAN_ALL_10 || this.current_scan === SCAN_7_9 || this.current_scan === SCAN_6 || this.current_scan === SCAN_2_YEARS));
    this.grid?.api?.setColumnsVisible(['last_alert_date'], (this.current_scan === SCAN_2_YEARS));

    //Now resize the columns
    setTimeout(() => {
      this.grid?.api?.sizeColumnsToFit();
    }, 100);
    
    this.set_grid_data();
  }
  set_grid_data() {
    // console.log('set_grid_data()', this.current_scan);
    //Get the data for the current scan
    let scan = this.scansMap[this.current_scan];
    //Inside a setTimeout so it executes on the next cycle
    setTimeout(() => {
      if (!scan) {
        this.data = [];
      }
      else {
        if (scan.group === SCAN_ALL_10) {
          //Special case for the 10+ scan
          this.data = this.get_scan_data_over_10();
        }
        else if (scan.group === SCAN_7_9) {
          //Special case for the 7-9 scan
          this.data = this.get_scan_data_7_9();
        }
        else if (scan.group === SCAN_6) {
          //Special case for the 6 scan
          this.data = this.get_scan_data_6();
        }
        else if (scan.group === SCAN_2_YEARS) {
          //Special case for the 2+ years scan
          this.data = this.get_scan_data_2_years();
        }
        else {
          this.data = scan.parsed_data || [];
        }
      }

      if (this.grid.api) {
        // this.grid.api.refreshCells({
        //   force: true,
        //   columns: ['expiration', 'call_put']
        // });
        this.grid.api.redrawRows();
      }
    }, 100);
  }
  parse_data(scan: string, data: any[]) {
    if (!data?.length) return [];

    let today = DateTime.now().startOf('day').toFormat('MM/dd/yyyy');
    let parsed_data = [] as any;
    // console.log('parse_data()', data);

    _.each(data, (d: any) => {
      //! This parsing is form data coming straight from the db
      // if (!d || !d._) return;
      
      // //Parse symbol
      // d.symbol = d._;

      // //Parse exchange_time
      // if (d.Exchange_TimeMS) {
      //   d.exchange_time = +d.Exchange_TimeMS;
      //   let dt_ms = today + d.exchange_time;
      //   d.exchange_time_ui = DateTime.fromMillis(dt_ms).toFormat('HH:mm:ss a');
      // }
      // else if (d.ExchangeMessageTimeStamp) {
      //   d.exchange_time = +d.ExchangeMessageTimeStamp;
      //   let dt_ms = today + d.exchange_time;
      //   d.exchange_time_ui = DateTime.fromMillis(dt_ms).toFormat('HH:mm:ss a');
      // }

      // //Parse expiration_week and expiration_ui
      // if (!d.expiration_week) {
      //   d.expiration_dt = DateTime.fromISO(d.Expiration);
      //   d.expiration_week = d.expiration_dt.weekNumber;
      //   d.expiration_ui = d.expiration_dt.toFormat('MM/dd/yyyy');
      // }

      // //Parse target
      // if (d.Target1) {
      //   d.Target1 = _.round(d.Target1, 2);
      //   if (!_.isInteger(d.Target1)) {
      //     d.Target1 = d.Target1.toFixed(2);
      //   }
      // }

      // //Parse price
      // if (d.TradePrice) {
      //   d.TradePrice = _.round(d.TradePrice, 2);
      //   if (!_.isInteger(d.TradePrice)) {
      //     d.TradePrice = d.TradePrice.toFixed(2);
      //   }
      // }

      if (!d) return;

      //Add the scan
      d.scan = scan;

      //Parse the id
      if (d._id?.value) {
        d.Id = d._id.value;
      }
      else {
        d.Id = d.Symbol.value;
      }

      //Parse symbol
      if (d.Symbol?.value) {
        d.stock_symbol = d.Symbol.value.replace(/\d+/g, '').slice(0, -1);
      }

      //Parse expiration week
      if (!d.expiration_week && d.Expiration?.value) {
        let expiration_dt = DateTime.fromFormat(d.Expiration.value, 'MM/dd/yyyy');
        d.expiration_week = expiration_dt.weekNumber;
      }

      //Parse exchange time
      if (d.ExchangeMessageTimeStamp?.value) {
        let dt = DateTime.fromFormat(`${today} ${d.ExchangeMessageTimeStamp.value}`, 'MM/dd/yyyy hh:mm:ss a');
        d.exchange_time = dt.valueOf();
        d.exchange_time_ui = d.ExchangeMessageTimeStamp.value;
      }
      else if (d.Exchange_TimeMS?.value) {
        let dt = DateTime.fromFormat(`${today} ${d.Exchange_TimeMS.value}`, 'MM/dd/yyyy hh:mm:ss a');
        d.exchange_time = dt.valueOf();
        d.exchange_time_ui = d.Exchange_TimeMS.value;
      }

      //Parse last alert date
      d.last_alert_date = d.EnterDates_End?.value ? new Date(d.EnterDates_End.value).valueOf() : 0;
      d.last_alert_date_DT = DateTime.fromMillis(d.last_alert_date);
      d.last_alert_years = Math.abs(d.last_alert_date_DT.diffNow().as('years'));
      // console.log("last alert", d.EnterDates_End?.value, d.last_alert_years);

      parsed_data.push(d);
      // console.log(d);
    });
    return parsed_data;
  }

  /*** Grid ***/
  rank_allowed() {
    let allowed = false;
    if (this._userData?.scanner_slug === 'option-sniper-t3') {
      allowed = true;
    }
    return allowed;
  }
  reports_allowed() {
    let allowed = false;
    if (this._userData?.scanner_slug === 'option-sniper-t3') {
      allowed = true;
    }
    return allowed;
  }
  number_filter_params() {
    return {
      defaultOption: 'greaterThanOrEqual',
    }
  }
  init_grid() {
    this.current_week = DateTime.now().weekNumber;
    this.grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        defaultColDef: {
          floatingFilter: true,
          filter: 'agTextColumnFilter',
          cellStyle: { 'text-align': 'center'}
        },
        getRowId: (params: any) => {
          return params.data.Id;
        },
        getRowStyle: (params: any) => {
          let data = params.data;
          let rstyle = this.get_rank_color_row(data.Rank?.value);
          return {
            background: rstyle.color,
            
          }
        }
      },
      columnDefs: [
        {
          field: "scan",
          headerName: 'Scan',
          cellStyle: {
            'text-align': 'left',
          },
          minWidth: 180
        },
        {
          field: "stock_symbol",
          headerName: 'Symbol',
          cellStyle: {
            'text-align': 'center',
            // color: '#976eff',
            'font-weight': this.darkMode ? 400 : 500
          }
        },
        {
          field: "MultiTrade_Volume.value",
          headerName: 'Quantity',
          filter: 'agNumberColumnFilter',
          filterParams: this.number_filter_params(),
        },
        {
          field: "Expiration.value",
          headerName: 'Expiration',
          cellStyle: (params: any) => {
            
            let style = { 'text-align': 'center', color: '' };
            // if (this.darkMode) {
            //   style.color = '#d8d8d8';
            // }
            // if (this.current_week === params.data.expiration_week) {
            //   style.color = 'orange'
            // }
            return style;
          }
        },
        {
          field: "Strike.value",
          headerName: 'Strike',
          filter: 'agNumberColumnFilter',
          filterParams: this.number_filter_params(),
        },
        {
          field: "Type.value",
          headerName: 'Call/Put',
          cellStyle: (params: any) => {
            let style = { color: '#d8d8d8', 'text-align': 'center' };
            switch(params.value) {
              case 'call':
              case 'Call':
                style.color = this.darkMode ? '#33cf9d' : '#0c9c0c';
                break;
              case 'put':
              case 'Put':
                style.color = this.darkMode ? '#d63939' : '#cb0000';
                break;
              default:
                break;
            }
            return style;
          }
        },
        {
          field: "TradePrice.value",
          headerName: 'Price',
          filter: 'agNumberColumnFilter',
          filterParams: this.number_filter_params(),
          valueGetter: (params: any) => {
            let d = params.data;
            let val = _.get(d, 'TradePrice.value', null);
            if (val === null) return null;
            else return +val;
          }
        },
        {
          field: "Target1.value",
          headerName: 'Target',
          filter: 'agNumberColumnFilter',
          filterParams: this.number_filter_params(),
        },
        {
          field: "exchange_time",
          headerName: 'Timestamp',
          sort: 'desc',
          width: 130,
          suppressSizeToFit: true,
          cellRenderer: (params: any) => {
            const value = params.data.exchange_time_ui || '';
            return `<span>${value}</span>`;
          }
        },
        {
          field: "Rank.value",
          headerName: 'Rank',
          filter: 'agNumberColumnFilter',
          filterParams: this.number_filter_params(),
          width: 90,
          suppressSizeToFit: true,
          hide: !this.rank_allowed(),
          cellRenderer: (params: any) => {
            const value = params.value || '';
            if (!value) return '';

            let up_1 = `<i class="fa-solid fa-angle-up" style="font-size: 18px; line-height: 12px"></i>`;
            let up_2 = `<i class="fa-solid fa-angles-up" style="font-size: 18px; line-height: 12px"></i>`;
            let icon = '';
            let res = this.get_rank_color(params.data);
            if (res.count === 2) {
              icon = up_2;
            }
            else if (res.count === 1) {
              icon = up_1;
            }
            return `
              <div style="display: flex; justify-content: center; align-items: center">
                <span style="flex: 1 0 auto">${value}</span>
                <span style="flex: 0 0 auto; color: ${res.color}; margin-right: 4px">${icon}</span>
              </div>
            `;
          }
        },
        {
          field: "TradeCount.value",
          headerName: "# of Trades",
          filter: 'agNumberColumnFilter',
          filterParams: this.number_filter_params(),
        },
        {
          field: "last_alert_date",
          headerName: "Last Alert",
          hide: true,
          cellRenderer: (params: any) => {
            if (params.value === 0) return '';
            return params.data.last_alert_date_DT.toFormat('MM/dd/yyyy');
          }
        },
      ]
    }
  }
  on_grid_ready(e: any) {
    // console.log('on_grid_ready()');
    this.grid.api = e.api;
    this.grid.colApi = e.colApi;
    this.grid.api.sizeColumnsToFit();
  }
  on_model_updated(e: any) {
    //Get the filtered rows from the grid
    if (this.grid.api) {
      this.filtered_data = [];
      this.grid.api.forEachNodeAfterFilter((node: any) => {
        this.filtered_data.push(node.data);
      });
    }
  }
  get_rank_color(data: any) {
    let result = {
      color: 'none',
      count: 0
    };
    // let color = 'none';
    // const yellow = 'linear-gradient(90deg, #afaf00 20%, transparent  99%)';//'#ffff0066';
    // const dark_red = 'linear-gradient(90deg, #800f00 20%, transparent  99%)'; //'#df000073';
    // const red = 'linear-gradient(90deg, #a31300 20%, transparent  99%)'; //'#f02b1175';
    // const blue = 'linear-gradient(90deg, #14356d 20%, transparent  99%)'; //'#6464ff6e';
    // const dark_green = 'linear-gradient(90deg, #006d00 20%, transparent  99%)';//'#00ff0057';
    // const green = 'linear-gradient(90deg, #008b00 20%, transparent  99%)';//'#008b00';
    // const orange = 'linear-gradient(90deg, #bb6c00 20%, transparent  99%)';//'orange';
    const yellow = '#ffc000';
    const dark_red = '#b42512';
    const red = '#da9595';
    const blue = '#14356d';
    const dark_green = '#1E6E6B';//'#006d00';
    const green = '#008b00';
    const orange = 'gray';//'#bb6c00';

    const rank = data.Rank?.value;
    const trade_count = data.TradeCount?.value;

    switch(this.current_scan) {
      // case 'Moonshot+Default': 
      // case 'ETF Scanner+Default':
      // case 'DayTraderScanner+Default': {
      //   if (rank >= 5) {
      //     result.color = yellow;
      //     result.count = 2;
      //   }
      //   else if (trade_count >= 10) {
      //     result.color = dark_red;//red;
      //     result.count = 2;
      //   }
      //   else {
      //     result.color = orange;
      //     result.count = 1;
      //   }
      //   break;
      // }

      // case 'Moonshot+Over6': {
      //   if (rank >= 6 && rank <= 10) {
      //     if (trade_count >= 10) {
      //       result.color = dark_green;
      //       result.count = 2;
      //     }
      //     else {
      //       result.color = green;
      //       result.count = 1;
      //     }
      //   }
      //   break;
      // }

      // case 'SuperOptions+Default': {
      //   if (!rank) {
      //     result.color = blue;
      //     result.count = 0;
      //   }
      //   else if (rank >= 5) {
      //     result.color = yellow;
      //     result.count = 2;
      //   }
      //   else if (rank < 5) {
      //     if (trade_count >= 10) {
      //       result.color = dark_red;
      //       result.count = 2;
      //     }
      //     else {
      //       result.color = red;
      //       result.count = 1;
      //     }
      //   }
      //   break;
      // }

      // case 'SuperOptions+Over6':
      // case 'Option Hacker+Over6':
      // case 'Option Hacker 2+Over6': 
      // case 'ETF Scanner+Over6': {
      //   if (rank >= 6 && rank <= 10) {
      //     if (trade_count >= 10) {
      //       result.color = dark_green;
      //       result.count = 2;
      //     }
      //     else {
      //       result.color = green;
      //       result.count = 1;
      //     }
      //   }
      //   break;
      // }

      // case 'Option Hacker+Default': {
      //   if (!rank) {
      //     result.color = blue;
      //   }
      //   else if (rank >= 5) {
      //     result.color = yellow;
      //     result.count = 2;
      //   }
      //   else if (rank < 5) {
      //     if (trade_count >= 6) {
      //       result.color = dark_red;
      //       result.count = 2;
      //     }
      //     else {
      //       result.color = red;
      //       result.count = 1;
      //     }
      //   }
      //   break;
      // }

      // case 'Penny Spike+Default':
      // case 'Option Hacker 2+Default':
      // case 'WildWildWest+Default': {
      //   if (rank >= 5) {
      //     result.color = yellow;
      //     result.count = 2;
      //   }
      //   else if (rank === 4) {
      //     result.color = dark_red;//red;
      //     result.count = 2;
      //   }
      //   else {
      //     result.color = orange;
      //     result.count = 1;
      //   }
      //   break;
      // }

      // case SCAN_ALL_10: {
      //   result.color = green;
      //   result.count = 2;
      //   break;
      // }

      default: {
        if (rank >= 10) {
          result.color = dark_green;
          result.count = 2;
        }
        else if (rank >= 6) {
          result.color = green;
          result.count = 2;
        }
        else if (rank >= 5) {
          result.color = yellow;
          result.count = 2;
        }
        else if (rank >= 4) {
          result.color = dark_red;
          result.count = 1;
        }
        else if (rank >= 3) {
          result.color = red;
          result.count = 1;
        }
        else {
          result.color = orange;
          result.count = 1;
        }
        break;
      }
    }

    return result;
  }
  get_rank_color_new(rank: number) {
    const yellow = '#ffc000';
    const dark_red = '#b42512';
    const red = '#da9595';
    const blue = '#14356d';
    const dark_green = '#1E6E6B';//'#006d00';
    const green = '#008b00';
    const orange = 'gray';//'#bb6c00';

    let result = {
      color: 'none',
      count: 0
    };

    if (this.rank_allowed() !== true) {
      return result;
    }

    if (rank >= 10) {
      result.color = dark_green;
      result.count = 2;
    }
    else if (rank >= 6) {
      result.color = green;
      result.count = 2;
    }
    else if (rank >= 5) {
      result.color = yellow;
      result.count = 2;
    }
    else if (rank >= 4) {
      result.color = dark_red;
      result.count = 1;
    }
    else if (rank >= 3) {
      result.color = red;
      result.count = 1;
    }
    else {
      result.color = orange;
      result.count = 1;
    }
    return result;
  }
  get_rank_color_row(rank: number) {
    const yellow = 'linear-gradient(90deg, rgba(255,243,0,0.4) 16%, rgba(255,243,0,0) 100%)';//'rgb(255 251 0 / 26%)';
    const dark_red = 'linear-gradient(90deg, rgb(180 37 18 / 25%) 16%, rgb(180 37 18 / 0%) 100%)';//'rgb(180 37 18 / 25%)';
    const red = 'linear-gradient(90deg, rgb(255 95 95 / 35%) 16%, rgb(255 95 95 / 0%) 100%)';//'rgb(255 95 95 / 35%)';
    const blue = '#14356d';
    const dark_green = 'linear-gradient(90deg, rgb(30 110 107 / 53%) 16%, rgb(30 110 107 / 0%) 100%)';//'rgb(30 110 107 / 26%)';
    const green = 'linear-gradient(90deg, rgb(0 139 0 / 27%) 16%, rgb(0 139 0 / 0%) 100%)';//'rgb(0 139 0 / 27%)';
    const gray = 'linear-gradient(90deg, rgb(109 109 109 / 35%) 16%, rgb(109 109 109 / 0%) 100%)';//'rgb(109 109 109 / 35%)';

    const l_yellow = 'linear-gradient(90deg, rgb(255 202 0 / 53%) 16%, rgb(255 202 0 / 0%) 100%)';
    const l_dark_red = 'linear-gradient(90deg, rgb(255 36 36 / 61%) 16%, rgb(255 36 36 / 0%) 100%)';
    const l_red = 'linear-gradient(90deg, rgb(255 104 104 / 54%) 16%, rgb(255 104 104 / 0%) 100%)';
    const l_dark_green = 'linear-gradient(90deg, rgb(12 140 135 / 54%) 16%, rgb(12 140 135 / 0%) 100%)';
    const l_gray = 'linear-gradient(90deg, rgb(143 140 140 / 41%) 16%, rgb(143 140 140 / 0%) 100%)';

    let result = {
      color: 'rgba(0,0,0,0)',
      count: 0
    };

    if (this.rank_allowed() !== true) {
      return result;
    }

    if (rank >= 10) {
      result.color = this.darkMode ? dark_green : l_dark_green;
      result.count = 2;
    }
    else if (rank >= 6) {
      result.color = green;
      result.count = 2;
    }
    else if (rank >= 5) {
      result.color = this.darkMode ? yellow : l_yellow;
      result.count = 2;
    }
    else if (rank >= 4) {
      result.color = this.darkMode ? dark_red : l_dark_red;
      result.count = 1;
    }
    else if (rank >= 3) {
      result.color = this.darkMode ? red : l_red;
      result.count = 1;
    }
    else if (rank >= 1) {
      result.color = this.darkMode ? gray : l_gray;
      result.count = 1;
    }
    return result;
  }
  clear_all_filters() {
    if (this.grid.api) {
      this.grid.api.setFilterModel(null);
    }
  }

  /*** Custom Scans ***/
  get_combined_scan_id(item: any) {
    return `${item?.scan}::${item?.stock_symbol}`;
  }
  get_scan_data_6() {
    let data = [] as any[];
    _.each(this.scans, (scan: any) => {
      if (!scan || !scan.parsed_data || !scan.parsed_data.length || scan.type === 'custom') return;
      let d = _.filter(scan.parsed_data, (d: any) => {
        return d.Rank?.value === 6;
      });
      data = _.concat(data, d);
    });
    //Make sure the entries are unique
    data = _.uniqBy(data, (d: any) => {
      return this.get_combined_scan_id(d);
    });

    // console.log(data);
    return data;
  }
  get_scan_data_7_9() {
    let data = [] as any[];
    _.each(this.scans, (scan: any) => {
      if (!scan || !scan.parsed_data || !scan.parsed_data.length || scan.type === 'custom') return;
      let d = _.filter(scan.parsed_data, (d: any) => {
        return d.Rank?.value >= 7 && d.Rank?.value <= 9;
      });
      data = _.concat(data, d);
    });
    //Make sure the entries are unique
    data = _.uniqBy(data, (d: any) => {
      return this.get_combined_scan_id(d);
    });

    // console.log(data);
    return data;
  }
  get_scan_data_over_10() {
    let data = [] as any[];
    _.each(this.scans, (scan: any) => {
      if (!scan || !scan.parsed_data || !scan.parsed_data.length || scan.type === 'custom') return;
      let d = _.filter(scan.parsed_data, (d: any) => {
        return d.Rank?.value >= 10;
      });
      data = _.concat(data, d);
    });
    //Make sure the entries are unique
    data = _.uniqBy(data, (d: any) => {
      return this.get_combined_scan_id(d);
    });
    // console.log(data);
    return data;
  }
  get_scan_data_2_years() {
    let data = [] as any[];
    _.each(this.scans, (scan: any) => {
      if (!scan || !scan.parsed_data || !scan.parsed_data.length || scan.type === 'custom') return;
      let d = _.filter(scan.parsed_data, (d: any) => {
        return d.last_alert_years >= 2;
      });
      data = _.concat(data, d);
    });

    //Make sure the entries are unique
    data = _.uniqBy(data, (d: any) => {
      return d.Id;
    });
    // console.log(data);
    return data;
  }
  add_alert_to_custom_scan(scan: any, item: any) {
    if (!scan || !item) return;
    if (!scan.parsed_data) scan.parsed_data = [];
    let _item = _.find(scan.parsed_data, (_d: any) => {
      if (!_d) return false;
      return this.get_combined_scan_id(_d) === this.get_combined_scan_id(item);
    });
    if (!_item) {
      scan.parsed_data.push(item);
    }
  }

  /*** Reports ***/
  async on_show_reports_click() {
    this.init_report_grid();
    this.report = null;
    this.show_reports = true;
    this.init_scan_reports();
  }
  onSelectedReportChange(report: string) {
    if (!report || report === '') {
      //Clear the report data
      this.selected_report = null;
      this.report = null;
      this.report_data = [];
      return;
    }

    this.selected_report = report;
    this.init_scan_reports();
  }
  on_report_symbol_keyup(e: any) {
    if (e.key === 'Enter') {
      this.getReportData(this.report_symbol);
    }
  }
  set_report_data_from_type() {
    if (!this.report_symbol || !this.selected_report || !this.report) return;
    switch(this.report_type) {
      case 'all': {
        this.report_data = this.report.all.data;
        break;
      }
      case 'calls': {
        this.report_data = this.report.calls.data;
        break;
      }
      case 'puts': {
        this.report_data = this.report.puts.data;
        break;
      }
      default: {
        this.report_data = [];
        break;
      }
    }
    // console.log('report_data:', this.report_data);
  }
  // async getAvailableReports() {
  //   let res = await this.restService.get(`https://${this._baseUrl}/api/reports/get-reports?token=${this._token}`, 'Loading available reports ...');
  //   console.log(res);
  // }
  // async init_scan_reports(scan: any) {
  //   await this.getReportSymbols(scan);
  //   // await this.getAvailableReports();
  // }
  async init_scan_reports() {
    if (!this.selected_report) return;
    await this.getReportSymbols();
  }
  async getReportSymbols() {
    //1. Get all the symbols for the selected report
    let date = this.dateMode === 'live' ? this.today : this.dateMode;
    let url = [
      `https://${this._baseUrl}/api/reports/get-all-symbols?`,
      `group=${this.selected_report}`,
      `&type=report`,
      `&token=${this._token}`,
      `&date=${date}`
    ].join('');
    // console.log(url);

    let res: any = await this.restService.get(url, `Loading report symbols ...`);
    let data = res?.dataset?.data;
    this.report_symbols = [];
    data = _.orderBy(data, ['Symbol.value'], ['asc']);
    _.each(data, (d: any) => {
      if (!d || !d.formattedSymbol?.value) return;
      this.report_symbols.push({
        _symbol: d.Symbol.value,
        symbol: d.formattedSymbol.value
      });
    });
    // console.log('report_symbols:', this.report_symbols);

    if (this.report_symbol) {
      await this.getReportData(this.report_symbol);
    }
  }
  async getReportData(symbol: string) {
    this.report_data = [];
    this.report = null;
    if (!this.selected_report || !symbol) return;

    let scan = _.find(this.scans, (s: any) => { return s.report.riskReward === this.selected_report });
    if (!scan) {
      this.toast.error('Scan not found: ' + this.selected_report);
      return;
    }
    
    let _symbol = 'e' + symbol.toUpperCase();
    let date = this.dateMode === 'live' ? this.today : this.dateMode;
    let url = [
      `https://${this._baseUrl}/api/reports/get-symbol-data?`,
      `group=${this.selected_report}`,
      `&symbol=${_symbol}`,
      `&token=${this._token}`,
      `&scannerGroup=${scan.group}`,
      `&type=tickalert`,
      `&date=${date}`
    ].join('');
    // console.log(url);

    let res: any = await this.restService.get(url, `Getting report data for ${symbol}`);
    // console.log('Report backend res:', res);
    let data = _.get(res, 'dataset.riskRewards.data', null);
    // console.log('riskRewards:', data);
    if (!data || !data[0]) {
      this.toast.error(`Report data not found for symbol: ${symbol.toUpperCase()}`);
      return;
    }

    let d = data[0];
    console.log(d);
    this.report = {
      symbol: symbol,
      lastUpdated: DateTime.now().toFormat('MM/dd/yyyy hh:mm:ss a'),
      tradeCount: _.get(d['Stats.TradeCount'], 'value', 0),
      all: {
        rank: _.get(d['Stats.Rank'], 'value', 0),
        rankColor: this.get_rank_color_new(_.get(d['Stats.Rank'], 'value', 0)),
        data: [] as any[]
      },
      calls: {
        rank: _.get(d['CallStats.Rank'], 'value', 0),
        rankColor: this.get_rank_color_new(_.get(d['CallStats.Rank'], 'value', 0)),
        data: [] as any[]
      },
      puts: {
        rank: _.get(d['PutStats.Rank'], 'value', 0),
        rankColor: this.get_rank_color_new(_.get(d['PutStats.Rank'], 'value', 0)),
        data: [] as any[]
      }
    };

    _.each(d['Stats.Stats'], (s: any) => {
      if (!s) return;
      this.report.all.data.push({
        lossRate: this.sanitize_percent_value(_.get(s['LossRate'], 'value', 0)),
        winRate: this.sanitize_percent_value(_.get(s['WinRate'], 'value', 0)),
        stopLoss: this.sanitize_percent_value(_.get(s['StopLoss'], 'value', 0)),
        takeProfit: this.sanitize_percent_value(_.get(s['TakeProfit'], 'value', 0)),
        totalPnL: this.round_value(_.get(s.Total, 'PL.value', 0), 2),
        totalMeanPnL: this.round_value(_.get(s.Total, 'MeanPL.value', 0), 2),
        totalMeanHoldDays: this.round_value(_.get(s.Total, 'MeanHoldDays.value', 0), 2),
        winPnL: this.round_value(_.get(s.Win, 'PL.value', 0), 2),
        winMeanPnL: this.round_value(_.get(s.Win, 'MeanPL.value', 0), 2),
        winMeanHoldDays: this.round_value(_.get(s.Win, 'MeanHoldDays.value', 0), 2),
      });
    });

    _.each(d['CallStats.Stats'], (s: any) => {
      if (!s) return;
      this.report.calls.data.push({
        lossRate: this.sanitize_percent_value(_.get(s['LossRate'], 'value', 0)),
        winRate: this.sanitize_percent_value(_.get(s['WinRate'], 'value', 0)),
        stopLoss: this.sanitize_percent_value(_.get(s['StopLoss'], 'value', 0)),
        takeProfit: this.sanitize_percent_value(_.get(s['TakeProfit'], 'value', 0)),
        totalPnL: this.round_value(_.get(s.Total, 'PL.value', 0), 2),
        totalMeanPnL: this.round_value(_.get(s.Total, 'MeanPL.value', 0), 2),
        totalMeanHoldDays: this.round_value(_.get(s.Total, 'MeanHoldDays.value', 0), 2),
        winPnL: this.round_value(_.get(s.Win, 'PL.value', 0), 2),
        winMeanPnL: this.round_value(_.get(s.Win, 'MeanPL.value', 0), 2),
        winMeanHoldDays: this.round_value(_.get(s.Win, 'MeanHoldDays.value', 0), 2),
      });
    });

    _.each(d['PutStats.Stats'], (s: any) => {
      if (!s) return;
      this.report.puts.data.push({
        lossRate: this.sanitize_percent_value(_.get(s['LossRate'], 'value', 0)),
        winRate: this.sanitize_percent_value(_.get(s['WinRate'], 'value', 0)),
        stopLoss: this.sanitize_percent_value(_.get(s['StopLoss'], 'value', 0)),
        takeProfit: this.sanitize_percent_value(_.get(s['TakeProfit'], 'value', 0)),
        totalPnL: this.round_value(_.get(s.Total, 'PL.value', 0), 2),
        totalMeanPnL: this.round_value(_.get(s.Total, 'MeanPL.value', 0), 2),
        totalMeanHoldDays: this.round_value(_.get(s.Total, 'MeanHoldDays.value', 0), 2),
        winPnL: this.round_value(_.get(s.Win, 'PL.value', 0), 2),
        winMeanPnL: this.round_value(_.get(s.Win, 'MeanPL.value', 0), 2),
        winMeanHoldDays: this.round_value(_.get(s.Win, 'MeanHoldDays.value', 0), 2),
      });
    });

    console.log(this.report);

    //Set the report data
    this.set_report_data_from_type();
    // this.report_data = this.report.all.data;
  }
  sanitize_percent_value(value: any) {
    let val = '';
    let tokens = value.toString().split('%');
    let n = _.toNumber(tokens[0]);
    if (_.isNaN(n)) return val;

    val = _.round(n, 2) + '%';
    return val;
  }
  round_value(value: any, decimals: number) {
    let val = _.toNumber(value);
    if (_.isNaN(val) || value.toString().toLowerCase() === 'nan') return '';

    return _.round(val, decimals);
  }
  getReportRank() {
    let rank = 0;
    if (!this.report) return rank;
    switch(this.report_type) {
      case 'all': {
        rank = this.report.all.rank;
        break;
      }
      case 'calls': {
        rank = this.report.calls.rank;
        break;
      }
      case 'puts': {
        rank = this.report.puts.rank;
        break;
      }
      default: {
        break;
      }
    }
    return rank;
  }
  getReportProfitTargetText(profitTarget: string) {
    switch(profitTarget) {
      case '25%': return 'Early';
      case '50%': return 'Intermediate';
      case '100%': return 'Full';
      case '125%': return 'Kicker';
      default: return profitTarget;
    }
  }
  convertReportMeanHoldDays(holdDays: number) {
    let converted = (Math.round(holdDays * 2) / 2).toFixed(1);
    return converted;
  }
  roundReportWinRate(winRate: string) {
    let tokens = winRate.split('%');
    let n = _.toNumber(tokens[0]);
    if (_.isNaN(n)) return winRate;
    n = _.round(n, 0);
    return n + '%';
  }
  convertReportPnlToDollarValue(pnl: number) {
    if (!pnl) return pnl;
    return _.toInteger(pnl*100);
  }

  init_report_grid() {
    this.report_grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        defaultColDef: {
          floatingFilter: true,
          filter: 'agTextColumnFilter',
          cellStyle: { 'text-align': 'center'},
          cellDataType: 'text'
        }
      },
      columnDefs: [
        {
          field: "stopLoss",
          headerName: 'Stop Loss',
          width: 90,
          suppressSizeToFit: true,
          headerClass: ['gray-col-header'],
        },
        {
          field: "takeProfit",
          headerName: 'Profit Target',
          width: 110,
          suppressSizeToFit: true,
          headerClass: ['gray-col-header'],
        },
        {
          field: "winRate",
          headerName: 'Win Rate',
          width: 95,
          suppressSizeToFit: true,
          headerClass: ['green-col-header'],
        },
        {
          field: "winPnL",
          headerName: 'Win P&L',
          width: 95,
          suppressSizeToFit: true,
          headerClass: ['green-col-header'],
        },
        {
          field: "winMeanPnL",
          headerName: 'Win P&L (avg)',
          headerClass: ['green-col-header'],
        },
        {
          field: "winMeanHoldDays",
          headerName: 'Win Hold Days (avg)',
          headerClass: ['green-col-header'],
        },
        {
          field: "totalPnL",
          headerName: 'Total P&L',
          width: 100,
          suppressSizeToFit: true,
        },
        {
          field: "totalMeanPnL",
          headerName: 'Total P&L (avg)',
        },
        {
          field: "totalMeanHoldDays",
          headerName: 'Total Hold Days (avg)',
        }
      ]
    }
  }
  on_report_grid_ready(e: any) {
    // console.log('on_grid_ready()');
    this.report_grid.api = e.api;
    this.report_grid.colApi = e.colApi;
    this.report_grid.api.sizeColumnsToFit();
  }

}
